/**
 * The main-thread side of the live-data worker — feature spec
 * ``ui-track-load-headroom`` §2.5 (Release 1 Step 4); REQ-UI-193.
 *
 * :func:`createWorkerLiveSource` spawns the worker for one stream URL and
 * presents it to :func:`useSse` as a :type:`LiveBatchSource`: the two
 * connection events the Flow A state machine consumes (``open`` /
 * ``error``, mirrored from the worker's ``EventSource``), plus one
 * ``batch`` callback that receives the frames already parsed, in arrival
 * order, once per worker tick. ``close()`` tells the worker to deliver
 * what is pending, close its source, and then terminates the worker — one
 * worker per connection keeps the lifecycle identical to the
 * ``EventSource`` it replaces (a viewport change closes one and opens the
 * next).
 *
 * The worker is created through an injectable factory so the proxy is
 * unit-tested against a fake; the default builds the real module worker
 * through Vite's ``new Worker(new URL(…, import.meta.url))`` pipeline.
 * Construction can throw (no ``Worker`` in the environment, a CSP that
 * refuses it); the caller — :func:`useSse` — catches that and falls back to
 * a main-thread ``EventSource``, so a worker failure degrades to the
 * pre-Step-4 path rather than to no live data (CLAUDE.md §8.1).
 */
import type { SseMessage } from "@/map/sse-types";
import type { MainToWorkerMessage, WorkerToMainMessage } from "@/live/worker-protocol";

/** A connection-event listener (no payload: the hook only needs the edge). */
export type LiveConnectionListener = () => void;

/** What :func:`useSse` consumes when the stream is worker-backed. */
export interface LiveBatchSource {
  addEventListener(type: "open" | "error", listener: LiveConnectionListener): void;
  removeEventListener(type: "open" | "error", listener: LiveConnectionListener): void;
  /** The one batch consumer: every delivered frame, parsed, in arrival order. */
  onBatch(listener: (messages: readonly SseMessage[]) => void): void;
  /** A frame the worker could not parse or validate (the hook logs it). */
  onMalformed(listener: (eventName: string) => void): void;
  /** Deliver what is pending, close the stream, release the worker. */
  close(): void;
}

/** The slice of ``Worker`` the proxy uses; a test passes a fake. */
export interface WorkerLike {
  postMessage(message: MainToWorkerMessage): void;
  terminate(): void;
  onmessage: ((event: MessageEvent<WorkerToMainMessage>) => void) | null;
  onerror: ((event: ErrorEvent) => void) | null;
}

/** Options for :func:`createWorkerLiveSource`. */
export interface WorkerLiveSourceOptions {
  /** Build the worker; defaults to the real module worker. */
  readonly createWorker?: () => WorkerLike;
}

function createModuleWorker(): WorkerLike {
  return new Worker(new URL("./live-data.worker.ts", import.meta.url), {
    type: "module",
    name: "meridian-live-data",
  }) as unknown as WorkerLike;
}

/** Whether this environment can host the worker-backed stream at all. */
export function workerLiveSourceAvailable(): boolean {
  return typeof Worker !== "undefined";
}

/**
 * Open ``url`` in a fresh live-data worker and return the proxy. Throws
 * when the worker cannot be constructed.
 */
export function createWorkerLiveSource(
  url: string,
  options: WorkerLiveSourceOptions = {},
): LiveBatchSource {
  const worker = (options.createWorker ?? createModuleWorker)();
  const listeners = {
    open: new Set<LiveConnectionListener>(),
    error: new Set<LiveConnectionListener>(),
  };
  let batchListener: ((messages: readonly SseMessage[]) => void) | null = null;
  let malformedListener: ((eventName: string) => void) | null = null;
  let closed = false;

  worker.onmessage = (event) => {
    if (closed) {
      return;
    }
    const message = event.data;
    switch (message.type) {
      case "open":
        for (const listener of [...listeners.open]) listener();
        break;
      case "error":
        for (const listener of [...listeners.error]) listener();
        break;
      case "batch":
        batchListener?.(message.messages);
        break;
      case "malformed":
        malformedListener?.(message.eventName);
        break;
    }
  };
  // A worker that fails outright (a script error) reads as a dropped
  // connection: the state machine goes through its ladder and the
  // operator sees RECONNECTING / DISCONNECTED rather than a silent stall
  // (CLAUDE.md §5.4).
  worker.onerror = () => {
    if (closed) {
      return;
    }
    for (const listener of [...listeners.error]) listener();
  };
  worker.postMessage({ type: "open", url });

  return {
    addEventListener: (type, listener) => {
      listeners[type].add(listener);
    },
    removeEventListener: (type, listener) => {
      listeners[type].delete(listener);
    },
    onBatch: (listener) => {
      batchListener = listener;
    },
    onMalformed: (listener) => {
      malformedListener = listener;
    },
    close: () => {
      if (closed) {
        return;
      }
      closed = true;
      worker.postMessage({ type: "close" });
      worker.terminate();
    },
  };
}
